import React, { useEffect, useMemo } from "react";
import { Row, Col } from "react-bootstrap";
import { useFormContext } from "react-hook-form";
import moment from "moment";
import { FormattedMessage, useIntl } from "react-intl";

import errorToast from "helpers/errorToast";
import { getLabelWithTranslationFallback } from "helpers/getLabel";
import { getNowInPreferredTimezone, getPreferredTimezoneMinDate, parsePreferredWallClock } from "helpers/dateTimeHelper";
import {
  isConfirmationAfterQuotingDeadline,
  isQuotingDeadlineAfterMinimum,
} from "pages/shipper/RequestForm/utils/preferredDeadlineValidation";
import Alert from "components/molecules/Alert/Alert";
import DatePicker from "components/atoms/DatePicker/DatePicker";
import TimeInput from "components/atoms/TimeInput/TimeInput";
import { useGetQuoteRequestTimerSettings } from "api/queries/quotes/useGetQuoteRequestTimerSettings";
import usePermissions from "hooks/usePermissions";
import testIds from "constants/testIds";
import "./ProviderForm.scss";
import useGetMultiDrop from "../Schedule/hooks/useGetMultiDrop";
import useGetMultiStop from "../Schedule/hooks/useGetMultiStop";

type ProviderTimingFormProps = {
  isMiniTender?: boolean;
};

const DEFAULT_QUOTING_DEADLINE_HOURS = 24;
const DEFAULT_CONFIRMATION_TIME_DAYS = 7;

function ProviderTimingForm(props: ProviderTimingFormProps) {
  const { isMiniTender = false } = props;
  const { getValues, setValue, watch } = useFormContext();
  const intl = useIntl();
  const { parsedPermissions } = usePermissions();
  const preferredTimezone = parsedPermissions?.preferred_timezone;

  const minSelectableDate = useMemo(() => getPreferredTimezoneMinDate(preferredTimezone), [preferredTimezone]);

  const { isMultiStopQuoteRequest } = useGetMultiStop();
  const { isMultiDropQuoteRequest } = useGetMultiDrop();
  const isMultiStopOrDropQuoteRequest = isMultiStopQuoteRequest || isMultiDropQuoteRequest;
  const pickupDate = isMultiStopOrDropQuoteRequest
    ? getValues("schedule.stops.0.stop_after_date")
    : getValues("schedule.pickup_ready_date");
  const pickupTime =
    isMultiStopOrDropQuoteRequest && getValues("schedule.stops.0.stop_after_time")
      ? getValues("schedule.stops.0.stop_after_time")
      : getValues("schedule.pickup_ready_time");

  const { data: timingSettings, isLoading } = useGetQuoteRequestTimerSettings({
    refetchOnMount: false,
    onError: (err) =>
      errorToast(
        err,
        intl.formatMessage({
          id: "new-request.timing.couldNotRetrieveRequestTimer",
          defaultMessage: "Could not retrieve request timer settings. Please try again later.",
        }),
      ),
  });

  useEffect(() => {
    if (!timingSettings) return;
    setDefaultTimings(timingSettings);
  }, [timingSettings]);

  const setDefaultTimings = (settings: any) => {
    const { use_default_confirmation_time } = settings;

    if (!getValues("shipment.quoting_deadline_date")) {
      const defaultDeadlineDate = getNowInPreferredTimezone(preferredTimezone).add(DEFAULT_QUOTING_DEADLINE_HOURS, "hours");
      setValue(`shipment.quoting_deadline_date`, defaultDeadlineDate.format("YYYY-MM-DD"));
      setValue(`shipment.quoting_deadline_time`, defaultDeadlineDate.format("HH:mm:ss"));
      if (isMiniTender) {
        updateDefaultConfirmationTiming(settings, false, true);
      } else if (use_default_confirmation_time) {
        updateDefaultConfirmationTiming(settings);
      } else {
        updateDefaultConfirmationTiming(settings, false);
      }
    }
  };

  const updateDefaultConfirmationTiming = (settings: any, useSettingDefault = true, addHours = false) => {
    const { default_confirmation_time_days } = settings || timingSettings;

    if (getValues("shipment.quoting_deadline_date") && getValues("shipment.quoting_deadline_time")) {
      let defaultConfirmationDate = parsePreferredWallClock(
        getValues("shipment.quoting_deadline_date"),
        getValues("shipment.quoting_deadline_time"),
        preferredTimezone,
      );

      if (addHours) {
        defaultConfirmationDate = defaultConfirmationDate?.clone().add(1, "hour") ?? null;
      } else {
        const daysToAdd = useSettingDefault ? default_confirmation_time_days : DEFAULT_CONFIRMATION_TIME_DAYS;
        defaultConfirmationDate = getNowInPreferredTimezone(preferredTimezone).clone().add(daysToAdd, "days");
      }

      if (!defaultConfirmationDate) {
        return;
      }

      setValue(`shipment.confirmation_date`, defaultConfirmationDate.format("YYYY-MM-DD"));
      setValue(`shipment.confirmation_time`, defaultConfirmationDate.format("HH:mm:ss"));
    }
  };

  const handleQuoteDeadlineSelect = () => {
    if (isMiniTender) {
      updateDefaultConfirmationTiming(timingSettings, false, true);
    } else if (timingSettings?.use_default_confirmation_time) {
      updateDefaultConfirmationTiming(timingSettings);
    }
  };

  const isValidConfirmationDate = (value: any): boolean =>
    Boolean(
      getValues("shipment.quoting_deadline_date") &&
        isConfirmationAfterQuotingDeadline(
          getValues("shipment.quoting_deadline_date"),
          getValues("shipment.quoting_deadline_time"),
          value,
          getValues("shipment.confirmation_time"),
          preferredTimezone,
        ),
    );

  const isValidQuotingDate = (value: any): boolean => {
    if (!timingSettings) return true;

    return isQuotingDeadlineAfterMinimum(
      value,
      getValues("shipment.quoting_deadline_time"),
      timingSettings.quoting_deadline_minimum_time_minutes,
      preferredTimezone,
    );
  };

  return (
    <div className="flex-row">
      {!pickupDate && !isMiniTender ? (
        <Alert variant="warning">
          <FormattedMessage id="new-request.timing.pickupDateNotSelected" defaultMessage="Pickup date not selected" />
        </Alert>
      ) : (
        <>
          {!isLoading && timingSettings && (
            <div className="d-flex">
              <div className="d-flex provider-timing-container">
                <div className="w-300">
                  <div className="d-flex">
                    <div>
                      <div className="align-date d-flex flex-column mr-2">
                        <label className="required no-wrap">
                          {isMiniTender ? (
                            <FormattedMessage
                              id="new-request.quote-confirmation.schedule.validityTill"
                              defaultMessage="Validity till"
                            />
                          ) : (
                            getLabelWithTranslationFallback("Quoting deadline", {
                              translation: (
                                <FormattedMessage
                                  id="new-request.quote-confirmation.schedule.quotingDeadlineDate"
                                  defaultMessage="Quoting deadline date"
                                />
                              ),
                            })
                          )}
                        </label>
                        <div data-testid={testIds.REQUEST_FORM_PROVIDER_TIMING_QUOTING_DEADLINE_DATE}>
                          <DatePicker
                            name="shipment.quoting_deadline_date"
                            onDateSelect={handleQuoteDeadlineSelect}
                            minDate={minSelectableDate}
                            required
                            validations={{
                              validDate: (v: any) =>
                                isValidQuotingDate(v) ||
                                `Minimum quoting deadline should be more than ${timingSettings.quoting_deadline_minimum_time_minutes} minuites`,
                            }}
                            registerOptions={{
                              deps: "shipment.confirmation_date",
                            }}
                          />
                        </div>
                      </div>
                    </div>
                    <div>
                      <div className="align-date d-flex flex-column">
                        <label className="required no-wrap">
                          <FormattedMessage id="general.time" defaultMessage="time" />
                        </label>
                        <div data-testid={testIds.REQUEST_FORM_PROVIDER_TIMING_QUOTING_DEADLINE_TIME}>
                          <TimeInput
                            name="shipment.quoting_deadline_time"
                            onTimeSelect={handleQuoteDeadlineSelect}
                            required
                            registerOptions={{ deps: ["shipment.confirmation_date", "shipment.quoting_deadline_date"] }}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                  {!isMiniTender &&
                    getValues("shipment.quoting_deadline_date") &&
                    moment(
                      `${getValues("shipment.quoting_deadline_date")} ${getValues("shipment.quoting_deadline_time")}`,
                      "YYYY-MM-DD HH:mm:ss",
                    ).isAfter(moment(`${pickupDate} ${pickupTime}`, "YYYY-MM-DD HH:mm:ss")) && (
                      <Alert variant="warning" className="mt-2 mr-5">
                        {`The suggested quoting deadline exceeds the requested pickup date. (${pickupDate})`}
                      </Alert>
                    )}
                </div>
                <div>
                  {!isMiniTender && (
                    <div className="w-300 d-flex flex-column justify-content-center">
                      {!timingSettings.use_default_confirmation_time && (
                        <div className="d-flex">
                          <div>
                            <div className="align-date d-flex flex-column mr-2">
                              <label className="required no-wrap">
                                {getLabelWithTranslationFallback("Confirmation time", {
                                  translation: (
                                    <FormattedMessage
                                      id="new-request.quote-confirmation.schedule.confirmationDate"
                                      defaultMessage="Confirmation date"
                                    />
                                  ),
                                })}
                              </label>
                              <div data-testid={testIds.REQUEST_FORM_PROVIDER_TIMING_CONFIRMATION_DATE}>
                                <DatePicker
                                  name="shipment.confirmation_date"
                                  minDate={minSelectableDate}
                                  required
                                  validations={{
                                    validDate: (v: any) =>
                                      isValidConfirmationDate(v) ||
                                      "The confirmation date must be greater than quoting deadline",
                                  }}
                                />
                              </div>
                            </div>
                          </div>
                          <div>
                            <div className="align-date d-flex flex-column">
                              <label className="required no-wrap">
                                <FormattedMessage id="general.time" defaultMessage="time" />
                              </label>
                              <div data-testid={testIds.REQUEST_FORM_PROVIDER_TIMING_CONFIRMATION_TIME}>
                                <TimeInput
                                  name="shipment.confirmation_time"
                                  required
                                  registerOptions={{ deps: "shipment.confirmation_date" }}
                                />
                              </div>
                            </div>
                          </div>
                        </div>
                      )}
                      {timingSettings.use_default_confirmation_time && (
                        <>
                          {!getValues("shipment.quoting_deadline_date") ? (
                            <div>
                              <Alert>Please select a deadline date and time</Alert>
                            </div>
                          ) : (
                            <div>
                              <label>
                                {getLabelWithTranslationFallback("Confirmation time", {
                                  translation: (
                                    <FormattedMessage
                                      id="new-request.quote-confirmation.schedule.confirmationTime"
                                      defaultMessage="Confirmation time"
                                    />
                                  ),
                                })}
                              </label>
                              <p className="font-weight-bold mb-0">
                                {getValues("shipment.confirmation_date")} -{" "}
                                {moment(getValues("shipment.confirmation_time"), "HH:mm:ss").format("hh:mm A")}
                              </p>
                            </div>
                          )}
                        </>
                      )}
                      {watch("shipment.confirmation_date") &&
                        moment(
                          `${getValues("shipment.confirmation_date")} ${getValues("shipment.confirmation_time")}`,
                          "YYYY-MM-DD HH:mm:ss",
                        ).isAfter(moment(`${pickupDate} ${pickupTime}`, "YYYY-MM-DD HH:mm:ss")) && (
                          <Alert variant="warning" className="mt-2 mb-3 mr-5">
                            {`The suggested confirmation time exceeds the requested pickup date. (${pickupDate})`}
                          </Alert>
                        )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
          {isLoading && !timingSettings && (
            <Col md={6}>
              <Row>
                <Col className="pl-4">
                  <div className="skeleton-card">
                    <div>
                      <Row className="skeleton-row">
                        <span key={1} className="skeleton" />
                        <span key={2} className="skeleton" />
                      </Row>
                    </div>
                  </div>
                </Col>
              </Row>
            </Col>
          )}
          {!isLoading && !timingSettings && (
            <Col md={3}>
              <Alert variant="warning">Unable to fetch timing settings, please try again</Alert>
            </Col>
          )}
        </>
      )}
    </div>
  );
}

export default ProviderTimingForm;

ProviderTimingForm.defaultProps = {
  isMiniTender: false,
};
